/**
 * Convert RGBA array to HEX string with alpha
 * @param rgba [r, g, b, a] where r,g,b are 0-255 and a is 0-1
 */
export function rgbaToHex(rgba: number[]): string {
  const [r, g, b, a] = rgba;

  const toHex = (num: number) => {
    const hex = Math.round(num).toString(16).padStart(2, '0');
    return hex.toUpperCase();
  };

  const hexR = toHex(r);
  const hexG = toHex(g);
  const hexB = toHex(b);
  const hexA = toHex(a * 255); // Convert 0-1 to 0-255

  // Return 8-digit hex with alpha
  return `#${hexR}${hexG}${hexB}${hexA}`;
}

/**
 * Parse a hex color (#RGB, #RRGGBB or #RRGGBBAA, with or without leading #)
 * into an [r, g, b] triple (0-255). Returns null if the string can't be parsed.
 */
export function hexToRgb(hex: string): [number, number, number] | null {
  if (typeof hex !== 'string') {
    return null;
  }

  let value = hex.trim().replace(/^#/, '');

  // expand shorthand #RGB -> #RRGGBB
  if (value.length === 3) {
    value = value
      .split('')
      .map((c) => c + c)
      .join('');
  }

  // drop alpha channel if present (#RRGGBBAA)
  if (value.length === 8) {
    value = value.slice(0, 6);
  }

  if (!/^[0-9a-fA-F]{6}$/.test(value)) {
    return null;
  }

  return [
    parseInt(value.slice(0, 2), 16),
    parseInt(value.slice(2, 4), 16),
    parseInt(value.slice(4, 6), 16),
  ];
}

/**
 * WCAG relative luminance (0 = black, 1 = white) for an [r, g, b] triple (0-255).
 * https://www.w3.org/TR/WCAG20/#relativeluminancedef
 */
export function relativeLuminance([r, g, b]: [number, number, number]): number {
  const toLinear = (channel: number): number => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };

  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
}

// * Text colors chosen for contrast on an arbitrary background. Theme-independent
// * literals (there is no Tier-2 "always-dark text" token to pair with static-text-dark).
export const READABLE_TEXT_ON_LIGHT = '#0B0F19'; // near-black
export const READABLE_TEXT_ON_DARK = 'rgba(255, 255, 255, 0.95)'; // matches --color-static-text-dark

// * Luminance above this reads as a "light" background -> use dark text.
// * ~0.4 keeps mid-tone brand colors readable with light text.
const LIGHT_BACKGROUND_LUMINANCE_THRESHOLD = 0.4;

/**
 * Pick a readable text color (near-black or near-white) for a given background
 * color, based on the background's perceived luminance. Falls back to dark text
 * for unparseable / empty values.
 */
export function readableTextColor(
  background: string | null | undefined,
): string {
  const rgb = background ? hexToRgb(background) : null;

  if (!rgb) {
    return READABLE_TEXT_ON_LIGHT;
  }

  return relativeLuminance(rgb) > LIGHT_BACKGROUND_LUMINANCE_THRESHOLD
    ? READABLE_TEXT_ON_LIGHT
    : READABLE_TEXT_ON_DARK;
}

const SOLID_ALPHA_THRESHOLD = 0.999;

// ! alpha === 1 -> #RRGGBB
// ! alpha < 1   -> #RRGGBBAA - we do not want this value, this is the util for
// * if the alpha is 1, we return the hex without the alpha channel - this is for Pickr compatibility
export function rgbaToNormalizedHex(
  rgba: [number, number, number, number],
): string {
  const a = rgba[3];

  const hex = rgbaToHex(rgba);

  // * Solid -> #RRGGBB
  if (a >= SOLID_ALPHA_THRESHOLD) {
    return hex.slice(0, 7);
  }

  // * Semi-transparent -> #RRGGBBAA
  return hex;
}
